// ---------------------------------------------------------------------------
// Why Bring to Life refused a picture, read from the server's 422 (SYM-6096).
//
// The server already knows whether widening the crop can help: it tells the
// three cases apart (the picture itself is too small, it is too small for this
// placement's shape, or only the crop is). It sends that verdict, and for the
// crop case how much of the picture the crop has to cover, beside the sentence
// written for the customer. Everything here reads those; nothing here measures
// pixels -- that arithmetic is the server's, on purpose (see btlSource.ts).
// ---------------------------------------------------------------------------

export type BtlRefusalKind =
  | 'picture_too_small'
  | 'placement_too_small'
  | 'crop_too_small'
  | 'unknown';

/** What the crop has to cover, as a share of the picture on each axis. */
export interface CropMinimum {
  widthPct: number;
  heightPct: number;
}

export interface BtlRefusal {
  /** The sentence for the customer. */
  reason: string;
  kind: BtlRefusalKind;
  /** Present only when the crop is the problem. */
  minimum: CropMinimum | null;
}

type ErrorBag = Record<string, unknown> | undefined | null;

const first = (bag: ErrorBag, key: string): string | undefined => {
  const value = bag?.[key];
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return typeof value === 'string' ? value : undefined;
};

const KINDS: BtlRefusalKind[] = [
  'picture_too_small',
  'placement_too_small',
  'crop_too_small',
];

/**
 * The refusal in a 422's `errors`, or null when the response is not our
 * refusal at all (any other validation failure is a bug in what we sent and
 * must keep reading like one).
 *
 * A refusal from a server that predates the kind keys reads as 'unknown', and
 * the crop tool is still offered, exactly as before.
 */
export const readBtlRefusal = (errors: ErrorBag): BtlRefusal | null => {
  const reason = first(errors, 'video.source_resource_id');
  if (!reason) return null;

  const rawKind = first(errors, 'video.source_refusal.kind');
  const kind: BtlRefusalKind = KINDS.includes(rawKind as BtlRefusalKind)
    ? (rawKind as BtlRefusalKind)
    : 'unknown';

  const widthPct = Number(first(errors, 'video.source_refusal.min_width_pct'));
  const heightPct = Number(
    first(errors, 'video.source_refusal.min_height_pct')
  );
  const minimum =
    kind === 'crop_too_small' &&
    Number.isFinite(widthPct) &&
    Number.isFinite(heightPct) &&
    widthPct > 0 &&
    heightPct > 0
      ? { widthPct, heightPct }
      : null;

  return { reason, kind, minimum };
};

/** Widening the crop can fix it; anything else needs a different picture. */
export const cropCanFixIt = (refusal: BtlRefusal | null): boolean =>
  !refusal || refusal.kind === 'crop_too_small' || refusal.kind === 'unknown';

/**
 * Whether a crop, as a percentage of the picture, covers the minimum. The crop
 * box keeps the placement's shape while it is dragged, so one answer covers
 * both axes; a box that clears the width but not the height is still short.
 */
export const cropClearsMinimum = (
  crop: { width: number; height: number },
  minimum: CropMinimum
): boolean =>
  crop.width >= minimum.widthPct && crop.height >= minimum.heightPct;
